import React from "react"
import { useMemo, useState, useEffect } from "react"
import AppTabs from "components/ui/AppTabs"
import AppHeading1 from "components/ui/AppHeading1.jsx"
import AppSearchInput from "components/ui/AppSearchInput"
import AppButton from "components/ui/AppButton.jsx"
import AppPaginatedTable from "components/ui/AppPaginatedTable"
import AppModal from "components/ui/AppModal"
import { useDeliveryZoneColumns } from "./deliveryZoneColumns"
import AddNewMapAreas from "./PolygonBased/AddNewAreas"
import UpdateAreas from "./PolygonBased/UpdateAreas"
import DeleteMapAreas from "./PolygonBased/DeleteArea"
import AddZone from "./distanced-based-method/containers/AddZone"
import EditZone from "./distanced-based-method/containers/EditZone"
import DeleteZone from "./distanced-based-method/containers/DeleteZone"
import { useSelector } from "react-redux"
import { useShippingContext } from "store/contexts/ShippingMethodContext"
import useToggleStatus from "./PolygonBased/hooks/useChangeAreaStatus"
import useToggleZoneStatus from "./distanced-based-method/hooks/useToggleStatus"

import useGetStoreAddedMappedAreas from "features/shipping_methods/mapped-areas/PolygonBased/hooks/useGetStoreAddedMapAreas"
import useGetAllZones from "features/shipping_methods/mapped-areas/distanced-based-method/hooks/useGetAllZones"


const AreaListingContainer = () => {
    const { selectedStore, addedMappedAreas } = useSelector((state) => state.AreasReducer);
    const { shippingState } = useShippingContext();
    const getStoreMappedAddedAreas = useGetStoreAddedMappedAreas();
    const { getAddedDeliveryZones } = useGetAllZones();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [searchTerm, setSearchTerm] = useState("");
    const [rowsByTab, setRowsByTab] = useState({
        areas: [],
        zones: [],
    });
    const [selectedRow, setSelectedRow] = useState(null);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openAddZoneModal, setOpenAddZoneModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openEditZoneModal, setOpenEditZoneModal] = useState(false);
    const [openMapModal, setOpenMapModal] = useState(false);
    const [mapQuery, setMapQuery] = useState("");
    const tabs = [
        {
            label: "Polygon Based Areas",
            value: "areas",
        },
        {
            label: "Distance Based Zones",
            value: "zones",
        },
    ]
    const [value, setValue] = useState("areas")
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [isBulkDelete, setIsBulkDelete] = useState(false);
    const changeAreaStatus = useToggleStatus();
    const changeZoneStatus = useToggleZoneStatus();

    useEffect(() => {
        const storeId = selectedStore?.value;
        setSelectedKeys([]);
        if (!storeId) {
            setRowsByTab({ areas: [], zones: [] });
            return;
        }
        getStoreMappedAddedAreas({ storeId });
        getAddedDeliveryZones(storeId);
    }, [selectedStore?.value]);

    useEffect(() => {
        const mappedAreas = (addedMappedAreas || []).map((item) => ({
            id: item?._id,
            _id: item?._id,
            areaID: {
                areaNameEN: item?.mappedAreaInfo?.translations?.en?.name || "",
                areaNameAR: item?.mappedAreaInfo?.translations?.ar?.name || "",
            },
            cityID: {
                cityNameEN: item?.mappedAreaInfo?.city?.en || "",
                cityNameAR: item?.mappedAreaInfo?.city?.ar || "",
            },
            minOrder: item?.minOrder,
            deliveryFee: item?.deliveryFee,
            deliveryTime: item?.deliveryTime != null ? `${item.deliveryTime} mins` : "",
            isActive: item?.isActive,
            raw: item,
        }));

        const mappedZones = (shippingState?.distanceBasedAreas || []).map((zone) => ({
            id: zone?._id,
            _id: zone?._id,
            areaID: {
                areaNameEN: zone?.name || "",
                areaNameAR: zone?.name || "",
            },
            cityID: {
                cityNameEN: selectedStore?.label || "",
                cityNameAR: selectedStore?.label || "",
            },
            minOrder: zone?.minOrder,
            deliveryFee: zone?.deliveryFee,
            deliveryTime: zone?.deliveryTime != null ? `${zone.deliveryTime} mins` : "",
            isActive: zone?.isActive,
            raw: zone,
        }));

        setRowsByTab({
            areas: mappedAreas,
            zones: mappedZones,
        });
    }, [addedMappedAreas, shippingState?.distanceBasedAreas, selectedStore?.label]);

    const handleOpenAdd = () => {
        value=='areas' ? setOpenAddModal(true) : setOpenAddZoneModal(true);
    };

    const handleOpenEdit = (row) => {
        setSelectedRow(row);
        value=='areas' ? setOpenEditModal(true) : setOpenEditZoneModal(true);
    };

    const handleOpenDelete = (row) => {
        setSelectedRow(row);
        setIsBulkDelete(false);
        setOpenDeleteModal(true);
    };

    const buildMapQuery = (row) => {
        if (!row) return "";
        const areaName = row?.areaID?.areaNameEN || row?.areaID?.areaNameAR || "";
        const cityName = row?.cityID?.cityNameEN || row?.cityID?.cityNameAR || "";
        return `${areaName}${areaName && cityName ? ", " : ""}${cityName}`.trim();
    };

    const handleViewOnMap = () => {
        const targetRow = selectedRow || filteredData?.[0] || activeRows?.[0];
        const query = buildMapQuery(targetRow);
        if (!query) return;
        setMapQuery(query);
        setOpenMapModal(true);
    };

    

    const handleStatusChange = (row, val) => {
        if (value=='areas') {
            handleStatusChangeArea(row, val);
        } else {
            handleStatusChangeZone(row, val);
        }
    };

    const handleStatusChangeArea = (row, val) => {
        const payload = {
            mappedAreaBaseDeliveryMethodId: row?._id,
            isActive: !val,
          };
          changeAreaStatus({ payload, onClose: () => {} });
    };

    const handleStatusChangeZone = (row, val) => {
        const payload = {
            distanceBaseDeliveryMethodId: row?._id,
            isActive: !val,
          };
          changeZoneStatus({ payload, onClose: () => {} });
    };

    const columns = useDeliveryZoneColumns({
        onEdit: handleOpenEdit,
        onDelete: handleOpenDelete,
        onStatusChange:(row, val) => {
            handleStatusChange(row, val);
        }
    });

    const activeRows = rowsByTab[value] || [];

    const filteredData = useMemo(() => {
        const term = searchTerm?.trim()?.toLowerCase();
        if (!term) return activeRows;
        return activeRows.filter((item) => {
            const areaEn = item?.areaID?.areaNameEN || "";
            const areaAr = item?.areaID?.areaNameAR || "";
            const cityEn = item?.cityID?.cityNameEN || "";
            const cityAr = item?.cityID?.cityNameAR || "";
            return [areaEn, areaAr, cityEn, cityAr].some((value) =>
                value.toLowerCase().includes(term)
            );
        });
    }, [activeRows, searchTerm]);

    // Selection is held as ids so it survives paging and searching. The rows are
    // resolved from the full list, not the filtered one, so narrowing the search
    // never silently drops something the user already ticked.
    const selectedRows = useMemo(
        () => activeRows.filter((row) => selectedKeys.includes(row?._id)),
        [activeRows, selectedKeys]
    );

    const handleOpenBulkDelete = () => {
        if (selectedRows.length === 0) return;
        setIsBulkDelete(true);
        setOpenDeleteModal(true);
    };

    const areasToDelete = isBulkDelete
        ? selectedRows.map((row) => row?.raw).filter(Boolean)
        : selectedRow?.raw
            ? [selectedRow.raw]
            : [];


    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="flex-shrink-0">
              <AppTabs
                  tabs={tabs}
                  value={value}
                  onChange={(nextValue) => {
                      setValue(nextValue);
                      setPage(1);
                      setSearchTerm("");
                      setOpenDeleteModal(false);
                      setSelectedRow(null);
                      setSelectedKeys([]);
                  }}
              />
            </div>
            <div className="min-h-0 flex-1">
            <div className="my-3 flex flex-col md:flex-row flex-shrink-0 items-center justify-between">
            <AppHeading1>{`${value=='areas' ? 'Areas' : 'Zones'}`} for {selectedStore?.label || "-"}</AppHeading1>
            <div className="flex flex-col md:flex-row items-center gap-3">
            <AppSearchInput
                value={searchTerm}
                placeholder="Search item..."
                onInput={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                }}
            />
            <div className="md:mt-0 mt-3 flex gap-3">
                <AppButton onClick={handleOpenAdd}>
                    Add Delivery {`${value=='areas' ? 'Area' : 'Zone'}`}
                </AppButton>
                <AppButton
                    variant="secondary"
                    onClick={handleViewOnMap}
                    disabled={activeRows.length === 0}
                >
                    View on Map
                </AppButton>
                {value == 'areas' && selectedKeys.length > 0 && (
                    <>
                        <AppButton variant="danger" onClick={handleOpenBulkDelete}>
                            Delete Selected ({selectedKeys.length})
                        </AppButton>
                        <AppButton variant="secondary" onClick={() => setSelectedKeys([])}>
                            Clear selection
                        </AppButton>
                    </>
                )}
            </div>
            
            </div>
        </div>
        <div className="min-h-0 flex-1 bg-white">
            <AppPaginatedTable
                data={filteredData}
                total={filteredData.length}
                page={page}
                pageSize={pageSize}
                clientSidePagination={true}
                onPageChange={(p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                }}
                columns={columns}
                selectable={value == 'areas'}
                selectedRowKeys={selectedKeys}
                onSelectionChange={setSelectedKeys}
            />
        </div>

        </div>
        <AddNewMapAreas open={openAddModal} onClose={() => setOpenAddModal(false)} />
        <AddZone open={openAddZoneModal} onClose={() => setOpenAddZoneModal(false)} />
        <UpdateAreas
                    open={openEditModal}
                    onClose={() => {
                        setOpenEditModal(false);
                        setSelectedRow(null);
                    }}
                    selectedAreas={selectedRow?.raw}
                />
        <EditZone
                    open={openEditZoneModal}
                    onClose={() => {
                        setOpenEditZoneModal(false);
                        setSelectedRow(null);
                    }}
                    selectedDataRows={selectedRow?.raw}
                />
        <AppModal
            open={openDeleteModal}
            onClose={() => {
                setOpenDeleteModal(false);
                setSelectedRow(null);
                setIsBulkDelete(false);
            }}
            width={550}
            title={null}
            footer={null}
        >
            {value=='areas' ? <DeleteMapAreas
                onClose={() => {
                    setOpenDeleteModal(false);
                    setSelectedRow(null);
                    setIsBulkDelete(false);
                }}
                selectedAreas={areasToDelete}
                setSelectedRowsKeys={setSelectedKeys}
            /> : <DeleteZone
                onClose={() => {
                    setOpenDeleteModal(false);
                    setSelectedRow(null);
                }}
                selectedZones={selectedRow?.raw ? [selectedRow.raw] : []}
                setSelectedRowsKeys={() => {}}
            />}
        </AppModal>
        <AppModal
            open={openMapModal}
            onClose={() => setOpenMapModal(false)}
            width={900}
            title={`${value === "areas" ? "Area" : "Zone"} Map`}
            footer={
                <div className="flex justify-end gap-2">
                    <AppButton variant="secondary" onClick={() => setOpenMapModal(false)}>
                        Close
                    </AppButton>
                    <AppButton
                        variant="primary"
                        onClick={() =>
                            window.open(
                                `https://maps.google.com/?q=${encodeURIComponent(mapQuery)}`,
                                "_blank"
                            )
                        }
                    >
                        Open in Google Maps
                    </AppButton>
                </div>
            }
        >
            <div className="h-[750px] w-full overflow-hidden rounded-xl border border-gray-200">
                {mapQuery ? (
                    <iframe
                        title="Delivery map preview"
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(
                            mapQuery
                        )}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
                        className="h-full w-full"
                    />
                ) : (
                    <div className="flex h-full items-center justify-center text-gray-500">
                        No map location available
                    </div>
                )}
            </div>
        </AppModal>
        </div>
    )
}

export default AreaListingContainer